import type { MinionKind } from '@/entities/minion-defs';

/**
 * Where things spawn in an arena (fractions of its size). Everything keeps
 * `away` px off the player's safe zone and `clear` px off rocks (docs/12 M9).
 */
export const SPAWNS = {
  /** Player ALWAYS spawns dead-center (user request) */
  player: [0.5, 0.5],
  boss: { at: [0.72, 0.5], away: 420, clear: 50 },
  portal: [0.9, 0.5],
  /** Arena guards: pushed off rocks AND away from the player (turret-lock fix) */
  turret: { away: 360, clear: 30 },
  bunker: { away: 320, clear: 30 },
  dome: { at: [0.6, 0.5], away: 360, clear: 36 },
  squad: { away: 260, clear: 26 },
  adds: { away: 280, clear: 26 },
} as const;

const SQUAD_KINDS: ReadonlyArray<MinionKind> =
  ['dasher', 'weaver', 'fly', 'mite', 'drone', 'splitter', 'sniper', 'elite'];
const ADD_KINDS: ReadonlyArray<MinionKind> = ['dasher', 'fly', 'mite', 'weaver'];

/**
 * Wave composition + formations (docs/12 C8/M2/M8). Counts and room gates
 * are in GAME.run.waves / GAME.run.bossAdds.
 */
export const WAVE_LAYOUT = {
  /** Stall failsafe: leftovers of a wave older than this (s) detonate */
  stallSeconds: 75,
  stallBlastSfx: 1.4,
  /** Yellow gems dropped at the ship when a wave clears */
  clearGems: 3,
  /** Advance-room segments field leaner squads (every segment fights) */
  advanceSquadMul: 0.8,
  /** Shake when the last wave falls and the boss arrives */
  bossArrivalShake: 10,
  /** Static bunker/dome formations on preset slots (docs/12 M8) */
  bunkerSlots: [
    [0.5, 0.5], [0.32, 0.3], [0.68, 0.3], [0.32, 0.7], [0.68, 0.7],
    [0.5, 0.22], [0.5, 0.78], [0.26, 0.5], [0.82, 0.5],
  ],
  bunkerMax: 4,
  /** +1 bunker per this many rooms past bunkerFromRoom / waves in */
  bunkerRoomStep: 2,
  bunkerWaveStep: 2,
  /** Slot stride between successive bunkers of one wave */
  bunkerSlotStride: 3,
  /** Light squad kind cycle — groups of `squadGroup` share a kind */
  squadKinds: SQUAD_KINDS,
  squadGroup: 3,
  squadKindStride: 5,
  /** Squad formations — picked by the wave hash (`hash % count`) */
  formations: {
    count: 4,
    /** 0: a column whose row cycles per wave */
    column: { x: 0.72, rows: [0.5, 0.28, 0.72] },
    /** 1: a vertical line from `top`, `span` tall */
    line: { x: 0.82, top: 0.18, span: 0.64 },
    /** 2: pincer — even members top, odd members bottom */
    pincer: { xEven: 0.62, xOdd: 0.78, yEven: 0.14, yOdd: 0.86 },
    /** 3: hash scatter — offsets of hash bits in 1/scale steps */
    scatter: { x: 0.5, xSteps: 45, y: 0.18, ySteps: 64, bits: 8, yShift: 3, scale: 100 },
  },
  /** Mid-fight side squads (docs/12 M2): small, light, from an arena edge */
  adds: {
    kinds: ADD_KINDS,
    leftChance: 0.5,
    edgeX: [0.12, 0.88],
    edgeY: [0.2, 0.8],
    size: [2, 4],
  },
} as const;
